import { isNumber, isString } from 'lodash';

import { type Clip, ShortUrlObjectType, type ShortUrlResponse } from '@air/api/types';

import { type ShortUrlDefaultSettingsResponse } from '~/swr-hooks/shortUrl/useGetDefaultSettings';
import { type AssetScopedShareLinkListItem } from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { getClipDisplayTitle } from '~/utils/getClipDisplayTitle';

export type AssetShareLinkSlotId = 'current' | `v${number}`;

export const EMPTY_ASSET_SHARE_LINK_OPTION_PREFIX = 'empty:';

export type AssetShareLinkOption = {
  chipLabel: string;
  isEnabled: boolean;
  /** Unique dropdown identity: short URL id, or `empty:{slotId}` when the slot has no link. */
  key: string;
  link: AssetScopedShareLinkListItem | null;
  slotId: AssetShareLinkSlotId;
  title: string;
};

export const getEmptyAssetShareLinkOptionKey = (slotId: AssetShareLinkSlotId): string =>
  `${EMPTY_ASSET_SHARE_LINK_OPTION_PREFIX}${slotId}`;

export const buildEmptySlotShareLinkDisplay = (defaultSettings: ShortUrlDefaultSettingsResponse): ShortUrlResponse => ({
  id: '',
  permissions: defaultSettings.permissions,
  requireAccount: defaultSettings.requireAccount,
  presentationSettings: defaultSettings.presentationSettings as ShortUrlResponse['presentationSettings'],
});

const createAssetShareLinkOption = (
  link: AssetScopedShareLinkListItem,
  slotId: AssetShareLinkSlotId,
  versionClips: readonly AssetShareLinkVersionClip[],
  assetVersionCount: Clip['assetVersionCount'] | undefined,
): AssetShareLinkOption => ({
  key: link.id,
  slotId,
  link,
  title: resolveAssetShareLinkSlotTitle(slotId, link, versionClips, assetVersionCount),
  chipLabel: getAssetShareLinkSlotChipLabel(slotId, assetVersionCount),
  isEnabled: isAssetShareLinkEnabled(link),
});

const createEmptyAssetShareLinkOption = (
  slotId: AssetShareLinkSlotId,
  versionClips: readonly AssetShareLinkVersionClip[],
  assetVersionCount: Clip['assetVersionCount'] | undefined,
): AssetShareLinkOption => ({
  key: getEmptyAssetShareLinkOptionKey(slotId),
  slotId,
  link: null,
  title: resolveAssetShareLinkSlotTitle(slotId, null, versionClips, assetVersionCount),
  chipLabel: getAssetShareLinkSlotChipLabel(slotId, assetVersionCount),
  isEnabled: false,
});

const getShareLinkVersion = (shortUrl: AssetScopedShareLinkListItem): number | undefined => {
  return 'version' in shortUrl && isNumber(shortUrl.version) ? shortUrl.version : undefined;
};

const isCurrentAssetShareLink = (item: AssetScopedShareLinkListItem): boolean => {
  return item.objectType === ShortUrlObjectType.asset && !item.targetClipId;
};

/** Pinned V{n} rows only — `version` alone is not sufficient (Current rows may also include it). */
const isVersionSlotShareLink = (item: AssetScopedShareLinkListItem): boolean => {
  if (isCurrentAssetShareLink(item)) {
    return false;
  }

  return item.objectType === ShortUrlObjectType.clip || !!item.targetClipId;
};

const resolveShareLinkVersion = (
  item: AssetScopedShareLinkListItem,
  versionClips: readonly Pick<Clip, 'id' | 'version'>[] = [],
): number | undefined => {
  const apiVersion = getShareLinkVersion(item);

  if (apiVersion !== undefined) {
    return apiVersion;
  }

  const clipVersion = versionClips.find((clip) => clip.id === getAssetShareLinkClipId(item))?.version;

  return isNumber(clipVersion) ? clipVersion : undefined;
};

/**
 * Builds one dropdown row per share link returned by the asset-scoped list endpoint.
 * Empty version slots are included when a version has no links yet (create flow).
 *
 * Option order matches the version dropdown UI: Current first, then version slots from
 * highest to lowest (Vn, Vn-1, …, V1) — not ascending V1, V2, … order.
 */
export const buildAssetShareLinkOptions = (
  items: AssetScopedShareLinkListItem[],
  assetVersionCount: Clip['assetVersionCount'] | undefined,
  versionClips: readonly AssetShareLinkVersionClip[] = [],
): AssetShareLinkOption[] => {
  const options: AssetShareLinkOption[] = [];
  const currentLinks: AssetScopedShareLinkListItem[] = [];
  const versionLinksByVersion = new Map<number, AssetScopedShareLinkListItem[]>();

  for (const item of items) {
    if (isCurrentAssetShareLink(item)) {
      currentLinks.push(item);
      continue;
    }

    const version = resolveShareLinkVersion(item, versionClips);

    if (version === undefined) {
      continue;
    }

    const versionLinks = versionLinksByVersion.get(version);

    if (versionLinks) {
      versionLinks.push(item);
    } else {
      versionLinksByVersion.set(version, [item]);
    }
  }

  for (const link of currentLinks) {
    options.push(createAssetShareLinkOption(link, 'current', versionClips, assetVersionCount));
  }
  if (currentLinks.length === 0) {
    options.push(createEmptyAssetShareLinkOption('current', versionClips, assetVersionCount));
  }

  const versionCount = assetVersionCount ?? 0;
  for (let version = versionCount; version >= 1; version -= 1) {
    const slotId = getAssetShareLinkSlotIdForClipVersion(version);

    if (!slotId) {
      continue;
    }

    const versionLinks = versionLinksByVersion.get(version) ?? [];

    for (const link of versionLinks) {
      options.push(createAssetShareLinkOption(link, slotId, versionClips, assetVersionCount));
    }

    if (versionLinks.length === 0) {
      options.push(createEmptyAssetShareLinkOption(slotId, versionClips, assetVersionCount));
    }
  }

  return options;
};

export const getAssetShareLinkOptionByKey = (
  options: AssetShareLinkOption[],
  key: string,
): AssetShareLinkOption | undefined => options.find((option) => option.key === key);

export const getAssetShareLinkOptionSelection = (selectedOption: AssetShareLinkOption | undefined) => ({
  selectedOptionKey: selectedOption?.key ?? '',
  selectedSlotId: selectedOption?.slotId ?? 'current',
  selectedSlotShortUrl: selectedOption?.link ?? null,
});

const pickPreferredAssetShareLinkOption = (candidates: AssetShareLinkOption[]): AssetShareLinkOption | undefined => {
  if (candidates.length === 0) {
    return undefined;
  }

  return candidates.find((option) => isAssetShareLinkEnabled(option.link)) ?? candidates[0];
};

const pickEnabledAssetShareLinkOptionKey = (candidates: AssetShareLinkOption[]): string | undefined => {
  const option = pickPreferredAssetShareLinkOption(candidates);

  return option && isAssetShareLinkEnabled(option.link) ? option.key : undefined;
};

/**
 * Picks the dropdown key for a slot: preferred existing link, else the empty-slot option.
 * Used after options refresh (e.g. create replaces `empty:v2` with a real short id).
 */
export const resolveSlotOptionKeyFromList = (
  options: AssetShareLinkOption[],
  slotId: AssetShareLinkSlotId,
): string | undefined => {
  const slotLinkOptions = options.filter((option) => option.slotId === slotId && option.link);
  const linkPick = pickPreferredAssetShareLinkOption(slotLinkOptions);

  if (linkPick) {
    return linkPick.key;
  }

  return options.find((option) => option.slotId === slotId && !option.link)?.key;
};

export type GetDefaultAssetShareLinkOptionKeyParams = {
  context: AssetShareLinkSelectionContext;
  options: AssetShareLinkOption[];
  /**
   * When true (share opened outside asset-details), default to the first enabled option in
   * `buildAssetShareLinkOptions` order: Current, then Vn → V1. If Current is disabled, a
   * higher version slot (e.g. V3) is chosen before a lower one (e.g. V1) — that matches the
   * dropdown and is intentional, not lowest-version-first.
   */
  preferEnabledShareLink?: boolean;
};

export const getDefaultAssetShareLinkOptionKey = ({
  context,
  options,
  preferEnabledShareLink = false,
}: GetDefaultAssetShareLinkOptionKeyParams): string => {
  if (options.length === 0) {
    return getEmptyAssetShareLinkOptionKey('current');
  }

  const resolveCurrentOptionKey = (): string => resolveSlotOptionKeyFromList(options, 'current') ?? options[0].key;

  if (preferEnabledShareLink) {
    // First enabled row in dropdown/build order (see GetDefaultAssetShareLinkOptionKeyParams).
    const enabledOptionKey = options.find((option) => isAssetShareLinkEnabled(option.link))?.key;

    return enabledOptionKey ?? resolveCurrentOptionKey();
  }

  const versionSlotId = getAssetShareLinkSlotIdForClipVersion(context.clipVersion);
  const currentOptions = options.filter((option) => option.slotId === 'current' && option.link);
  const versionOptions = versionSlotId
    ? options.filter((option) => option.slotId === versionSlotId && option.link)
    : [];

  if (context.isDefaultVersion) {
    return (
      pickEnabledAssetShareLinkOptionKey(currentOptions) ??
      pickEnabledAssetShareLinkOptionKey(versionOptions) ??
      resolveCurrentOptionKey()
    );
  }

  if (versionSlotId) {
    const resolvedVersionSlotKey = resolveSlotOptionKeyFromList(options, versionSlotId);

    if (resolvedVersionSlotKey) {
      return resolvedVersionSlotKey;
    }
  }

  return resolveCurrentOptionKey();
};

export const getAssetShareLinkClipId = (item: AssetScopedShareLinkListItem): string => {
  if (item.objectType === ShortUrlObjectType.clip) {
    return item.objectId;
  }

  if (!item.targetClipId) {
    throw new Error('Expected pinned asset share link with targetClipId');
  }

  return item.targetClipId;
};

const getVersionNumberFromSlotId = (slotId: Exclude<AssetShareLinkSlotId, 'current'>): number => {
  return Number(slotId.slice(1));
};

const getAssetShareLinkVersionLabel = (slotId: Exclude<AssetShareLinkSlotId, 'current'>): string => {
  return `V${getVersionNumberFromSlotId(slotId)}`;
};

export const getAssetShareLinkSlotDescription = (slotId: AssetShareLinkSlotId): string => {
  if (slotId === 'current') {
    return 'This link will always reflect the current version of the asset.';
  }

  return `This link will always show ${getAssetShareLinkVersionLabel(slotId)} of the asset.`;
};

export const getAssetShareLinkModalTitle = (slotId: AssetShareLinkSlotId): string => {
  if (slotId === 'current') {
    return 'Share link to asset';
  }

  return `Share link to ${getAssetShareLinkVersionLabel(slotId)}`;
};

export const getAssetShareLinkSlotChipLabel = (
  slotId: AssetShareLinkSlotId,
  assetVersionCount: Clip['assetVersionCount'] | undefined,
): string => {
  if (slotId === 'current') {
    const version = assetVersionCount ?? 1;

    return `Current · V${version}`;
  }

  return getAssetShareLinkVersionLabel(slotId);
};

export const getAssetShareLinkListItemTitle = (
  item: AssetScopedShareLinkListItem | null,
  fallbackTitle: string,
): string => {
  if (!item) {
    return fallbackTitle;
  }

  if ('name' in item && isString(item.name) && item.name) {
    return item.name;
  }

  if ('title' in item && isString(item.title) && item.title) {
    return item.title;
  }

  return fallbackTitle;
};

export type AssetShareLinkVersionClip = Pick<Clip, 'id' | 'version' | 'title' | 'importedName' | 'isDefault'>;

const getCurrentVersionClip = (
  versionClips: readonly AssetShareLinkVersionClip[],
  assetVersionCount: Clip['assetVersionCount'] | undefined,
): AssetShareLinkVersionClip | undefined => {
  const defaultClip = versionClips.find((clip) => clip.isDefault);

  if (defaultClip) {
    return defaultClip;
  }

  if (assetVersionCount) {
    const versionMatch = versionClips.find((clip) => clip.version === assetVersionCount);

    if (versionMatch) {
      return versionMatch;
    }
  }

  return [...versionClips].sort((left, right) => (right.version ?? 0) - (left.version ?? 0))[0];
};

const resolveAssetShareLinkSlotTitle = (
  slotId: AssetShareLinkSlotId,
  link: AssetScopedShareLinkListItem | null,
  versionClips: readonly AssetShareLinkVersionClip[],
  assetVersionCount: Clip['assetVersionCount'] | undefined,
): string => {
  const titleFromShareLink = getAssetShareLinkListItemTitle(link, '');

  if (titleFromShareLink) {
    return titleFromShareLink;
  }

  if (slotId === 'current') {
    const currentVersionClip = getCurrentVersionClip(versionClips, assetVersionCount);

    return currentVersionClip ? getClipDisplayTitle(currentVersionClip) : '';
  }

  const version = getVersionNumberFromSlotId(slotId);
  const versionClip = versionClips.find((clip) => clip.version === version);

  return versionClip ? getClipDisplayTitle(versionClip) : '';
};

export const isAssetShareLinkEnabled = (
  item: AssetScopedShareLinkListItem | null,
): item is AssetScopedShareLinkListItem => {
  return !!item && !item.disabled;
};

export const hasAnyEnabledAssetShareLinkOption = (options: AssetShareLinkOption[]): boolean => {
  return options.some((option) => isAssetShareLinkEnabled(option.link));
};

export type AssetShareLinkSelectionContext = {
  clipId: Clip['id'];
  clipVersion?: Clip['version'];
  isDefaultVersion: boolean;
};

export type AssetShareLinkSlotKind = 'current' | 'pinned';

export const getAssetShareLinkSlotKind = (slotId: AssetShareLinkSlotId): AssetShareLinkSlotKind =>
  slotId === 'current' ? 'current' : 'pinned';

/**
 * Whether a share link for the given slot should be asset-shaped.
 * Current slots are always asset; pinned slots depend on defaults, permissions, or stored type.
 */
export const shouldCreatePinnedShareLinkAsAsset = ({
  defaultSettings,
  objectType,
  canViewAllVersions,
  slotId,
}: {
  defaultSettings?: ShortUrlDefaultSettingsResponse | null;
  objectType?: ShortUrlObjectType.clip | ShortUrlObjectType.asset | null;
  canViewAllVersions?: boolean;
  slotId?: AssetShareLinkSlotId;
} = {}): boolean => {
  if (slotId === 'current') {
    return true;
  }

  return (
    objectType === ShortUrlObjectType.asset ||
    canViewAllVersions === true ||
    defaultSettings?.permissions.asset.canViewAllVersions === true
  );
};

/** Adds wire `objectType` when an update patches `permissions.asset.canViewAllVersions`. */
export const applyShowVersionsConversionToUpdateProps = <
  T extends {
    permissions?: { asset?: { canViewAllVersions?: boolean } };
    objectType?: ShortUrlObjectType.clip | ShortUrlObjectType.asset;
  },
>(
  link: AssetScopedShareLinkListItem,
  props: T,
): T => {
  const canViewAllVersions = props.permissions?.asset?.canViewAllVersions;

  if (typeof canViewAllVersions !== 'boolean' || isCurrentAssetShareLink(link)) {
    return props;
  }

  const asAsset = shouldCreatePinnedShareLinkAsAsset({ canViewAllVersions });

  return {
    ...props,
    objectType: asAsset ? ShortUrlObjectType.asset : ShortUrlObjectType.clip,
  };
};

export const resolveShareLinkObjectType = ({
  link,
  slotId,
  defaultSettings,
}: {
  link?: AssetScopedShareLinkListItem | null;
  slotId: AssetShareLinkSlotId;
  defaultSettings?: ShortUrlDefaultSettingsResponse | null;
}): ShortUrlObjectType.asset | ShortUrlObjectType.clip => {
  if (link?.objectType) {
    return link.objectType;
  }

  return shouldCreatePinnedShareLinkAsAsset({ slotId, defaultSettings })
    ? ShortUrlObjectType.asset
    : ShortUrlObjectType.clip;
};

/**
 * Resolves the list/API `objectId` for a share link row.
 * Omit `objectType` to use the link's stored type (current row identity); pass the
 * desired type when computing fields after a clip ↔ asset conversion.
 */
export const getAssetShareLinkObjectId = (
  link: AssetScopedShareLinkListItem,
  assetId: string,
  objectType: ShortUrlObjectType.clip | ShortUrlObjectType.asset = link.objectType,
): string => {
  if (objectType === ShortUrlObjectType.asset) {
    return assetId;
  }

  return getAssetShareLinkClipId(link);
};

export const getAssetShareLinkTargetClipId = (
  link: AssetScopedShareLinkListItem,
  objectType: ShortUrlObjectType.clip | ShortUrlObjectType.asset,
): string | undefined => {
  if (objectType !== ShortUrlObjectType.asset) {
    return undefined;
  }

  const isPinnedLink = link.objectType === ShortUrlObjectType.clip || !!link.targetClipId;

  return isPinnedLink ? getAssetShareLinkClipId(link) : undefined;
};

export const getAssetShareLinkSlotIdForClipVersion = (
  version: Clip['version'] | undefined,
): Exclude<AssetShareLinkSlotId, 'current'> | null => {
  if (!version) {
    return null;
  }

  return `v${version}`;
};

export const getVersionClipIdForShareLinkSlot = (
  slotId: AssetShareLinkSlotId,
  context: AssetShareLinkSelectionContext,
  items: AssetScopedShareLinkListItem[],
  versionClips: readonly Pick<Clip, 'id' | 'version'>[] = [],
): string | null => {
  if (slotId === 'current') {
    return null;
  }

  const version = getVersionNumberFromSlotId(slotId);

  if (Number.isNaN(version)) {
    return null;
  }

  if (context.clipVersion === version) {
    return context.clipId;
  }

  const existingVersionLink = items.find(
    (item) => isVersionSlotShareLink(item) && resolveShareLinkVersion(item, versionClips) === version,
  );

  if (existingVersionLink) {
    return getAssetShareLinkClipId(existingVersionLink);
  }

  const versionClip = versionClips.find((clip) => clip.version === version);

  if (versionClip) {
    return versionClip.id;
  }

  return null;
};
